import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import supportApiService from '../../services/supportApiService';
import { toast } from 'react-toastify';

const SupportTokenRechargeHistory = () => {
  const { agentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [rechargeHistory, setRechargeHistory] = useState([]);
  const [agentInfo, setAgentInfo] = useState(null);
  const [filters, setFilters] = useState({
    search: ''
  });

  useEffect(() => {
    if (agentId) {
      fetchAgentRechargeHistory();
    }
  }, [agentId]);

  const fetchAgentRechargeHistory = async () => {
    try {
      setLoading(true);
      const response = await supportApiService.getTokenRechargeHistory(agentId);
      if (response.success) {
        setRechargeHistory(response.tokenRechargeHistory || []);
        setAgentInfo({
          agentId: response.agentId,
          agentEmail: response.username,
          agentName: 'Agent',
          totalRecharges: response.totalRecords || 0
        });
      } else {
        toast.error(response.message || 'Failed to fetch agent recharge history');
      }
    } catch (error) {
      console.error('Error fetching agent recharge history:', error);
      toast.error('Failed to fetch agent recharge history');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const filterRecharges = () => {
    return rechargeHistory.filter(recharge => {
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        return (
          recharge.tokenId?.toLowerCase().includes(searchLower) ||
          recharge.rechargeAmount?.toLowerCase().includes(searchLower) ||
          recharge.noOfTokensRecharge?.toLowerCase().includes(searchLower) ||
          recharge.rechargeOperatorName?.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  };

  const filteredRecharges = filterRecharges();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Agent Token Recharge History</h1>
            {agentInfo && (
              <p className="text-gray-600 mt-2">
                Agent ID: {agentInfo.agentId} ({agentInfo.agentEmail}) - {agentInfo.totalRecharges} total transactions
              </p>
            )}
          </div>
          <Link 
            to="/support/dashboard/token-recharge" 
            className="text-green-600 hover:text-green-800 font-medium"
          >
            ← Back to Token Recharge
          </Link>
        </div>
      </div>

      {/* Agent Info Card */}
      {agentInfo && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm font-medium text-green-800">Agent ID</div>
              <div className="text-green-900 font-semibold">{agentInfo.agentId}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-green-800">Agent Email</div>
              <div className="text-green-900">{agentInfo.agentEmail}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-green-800">Total Recharge Transactions</div>
              <div className="text-green-900 font-bold">{agentInfo.totalRecharges}</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-2">Search Transactions</label>
          <input
            type="text"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="Search by ID, Operator, etc..."
          />
        </div>
      </div>

      {/* Recharge History Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Token Recharge History ({filteredRecharges.length} records)
          </h2>
        </div>
        
        {filteredRecharges && filteredRecharges.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tokens Added</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount Paid</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recharged Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operator ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operator Name</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRecharges.map((recharge, index) => (
                  <tr key={recharge.tokenId || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {recharge.tokenId || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">
                      +{recharge.noOfTokensRecharge || '0'} Tokens
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      ₹{recharge.rechargeAmount || '0'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {recharge.rechargedDate || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {recharge.rechargeOperatorId || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {recharge.rechargeOperatorName || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-8 text-center">
            <div className="text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No recharge history found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {filters.search
                  ? 'No recharge transactions match the search term.'
                  : 'This agent has no recharge history.'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Summary Statistics */}
      {filteredRecharges.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recharge Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{filteredRecharges.length}</div>
              <div className="text-sm text-gray-600">Total Recharge Actions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {filteredRecharges.reduce((sum, r) => sum + (parseInt(r.noOfTokensRecharge) || 0), 0)}
              </div>
              <div className="text-sm text-gray-600">Total Tokens Recharged</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-700">
                ₹{filteredRecharges.reduce((sum, r) => sum + (parseFloat(r.rechargeAmount) || 0), 0).toFixed(2)}
              </div>
              <div className="text-sm text-gray-600">Total Amount Paid</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTokenRechargeHistory;
